// src/lib/manufacturing/timeStudyClient.ts

import { manufacturingFetch } from "@/lib/manufacturingClient";

export type TimeStudySession = {
  id: string;
  organization_id: string;
  plant_id: string | null;
  title: string;
  status: "open" | "closed";
  started_at: string;
  closed_at: string | null;
  notes: string;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
};

export type TimeStudyEntry = {
  id: string;
  organization_id: string;
  session_id: string;
  entry_kind: "recipe" | "build_model" | "freeform";
  recipe_id: string | null;
  build_model_id: string | null;
  subject_label: string;
  plant_id: string | null;
  unit_id: string | null;
  personnel_label: string | null;
  duration_seconds: number;
  quantity: number | null;
  quantity_unit: string | null;
  notes: string;
  source: "manual" | "dictation";
  transcript_excerpt: string | null;
  occurred_at: string;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
};

export type TimeStudyDraftEntry = {
  subject_label: string;
  duration_seconds: number;
  personnel_label?: string | null;
  quantity?: number | null;
  quantity_unit?: string | null;
  notes?: string | null;
  recipe_id?: string | null;
  entry_kind?: "recipe" | "build_model" | "freeform";
};

export function listTimeStudySessions(opts?: { status?: "open" | "closed" }) {
  const q = new URLSearchParams();
  if (opts?.status) q.set("status", opts.status);
  const qs = q.toString();
  return manufacturingFetch<{ ok: true; sessions: TimeStudySession[] }>(
    qs ? `/api/mfg/time-study/sessions?${qs}` : "/api/mfg/time-study/sessions",
    { method: "GET" },
  );
}

export function createTimeStudySession(payload: {
  title?: string;
  plant_id?: string | null;
  notes?: string;
}) {
  return manufacturingFetch<{ ok: true; session: TimeStudySession }>(
    "/api/mfg/time-study/sessions/create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function closeTimeStudySession(sessionId: string) {
  return manufacturingFetch<{ ok: true; session: TimeStudySession }>(
    "/api/mfg/time-study/sessions/close",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    },
  );
}

export function listTimeStudyEntries(sessionId: string) {
  const q = new URLSearchParams({ session_id: sessionId });
  return manufacturingFetch<{ ok: true; entries: TimeStudyEntry[] }>(
    `/api/mfg/time-study/entries?${q.toString()}`,
    { method: "GET" },
  );
}

export function createTimeStudyEntry(payload: {
  session_id: string;
  subject_label: string;
  duration_seconds?: number;
  duration_minutes?: number;
  entry_kind?: "recipe" | "build_model" | "freeform";
  recipe_id?: string | null;
  build_model_id?: string | null;
  plant_id?: string | null;
  unit_id?: string | null;
  personnel_label?: string | null;
  quantity?: number | null;
  quantity_unit?: string | null;
  notes?: string | null;
  source?: "manual" | "dictation";
  transcript_excerpt?: string | null;
}) {
  return manufacturingFetch<{ ok: true; entry: TimeStudyEntry }>(
    "/api/mfg/time-study/entries/create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function deleteTimeStudyEntry(entryId: string) {
  return manufacturingFetch<{ ok: true }>("/api/mfg/time-study/entries/delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entry_id: entryId }),
  });
}

export function updateTimeStudyEntry(payload: {
  entry_id: string;
  subject_label?: string;
  duration_seconds?: number;
  duration_minutes?: number;
  personnel_label?: string | null;
  quantity?: number | null;
  quantity_unit?: string | null;
  notes?: string | null;
}) {
  return manufacturingFetch<{ ok: true; entry: TimeStudyEntry }>(
    "/api/mfg/time-study/entries/update",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function extractTimeStudyFromTranscript(transcript: string) {
  return manufacturingFetch<{
    ok: true;
    drafts: TimeStudyDraftEntry[];
    outcome: string;
  }>("/api/mfg/time-study/extract-from-transcript", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ transcript }),
  });
}
